import { Injectable, OnDestroy, inject, signal, computed } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../app-config.service';
import { AuthenticationService } from './authenticate.service';
import {
  IdleHeartbeatMode,
  IdleSessionHeartbeatResponse,
  IdleSessionStatus,
} from '../models/idle-session.interface';

/** Set to true to re-enable idle session heartbeat polling and logout. */
const IDLE_HEARTBEAT_ENABLED = false;

const DEFAULT_POLL_SECONDS = 30;
const MIN_POLL_SECONDS = 10;
const MAX_POLL_SECONDS = 60;
const GRACE_WARNING_SECONDS = 10;
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
] as const;

/**
 * BI idle session heartbeat against Nest POST /auth/session/heartbeat.
 * Uses shared ATS Redis userSession.lastAction (check = poll, touch = activity).
 */
@Injectable({
  providedIn: 'root',
})
export class IdleSessionHeartbeatService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigService);
  private readonly authService = inject(AuthenticationService);

  private readonly _warningVisible = signal(false);
  private readonly _countdownSeconds = signal(0);
  private readonly _remainingSeconds = signal(0);
  private readonly _inGraceWarning = signal(false);

  readonly warningVisible = this._warningVisible.asReadonly();
  readonly countdownSeconds = this._countdownSeconds.asReadonly();
  readonly remainingSeconds = this._remainingSeconds.asReadonly();
  readonly inGraceWarning = this._inGraceWarning.asReadonly();
  readonly warningMessage = computed(() => {
    const seconds = this._countdownSeconds();
    return `You will be logged off in ${seconds} seconds due to inactivity.`;
  });

  private started = false;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private localCountdownTimer: ReturnType<typeof setInterval> | null = null;
  private activityAbort: AbortController | null = null;
  private inFlightSub: Subscription | null = null;
  private inFlightMode: IdleHeartbeatMode | null = null;
  private pingGeneration = 0;
  private loggingOut = false;
  private idleAfter = 1800;
  private warningLength = 30;
  private pollIntervalSec = DEFAULT_POLL_SECONDS;
  private lastTouchSentAt = 0;
  private touchThrottleMs = 15_000;

  start(): void {
    if (!IDLE_HEARTBEAT_ENABLED) {
      return;
    }
    if (typeof window === 'undefined' || this.started || !this.authService.getBearerToken()) {
      return;
    }
    this.started = true;
    this.loggingOut = false;
    this.bindActivityListeners();
    this.ping('check');
    this.schedulePoll();
  }

  stop(): void {
    this.started = false;
    this.pingGeneration += 1;
    this.clearPoll();
    this.clearLocalCountdown();
    this.unbindActivityListeners();
    this.inFlightSub?.unsubscribe();
    this.clearInFlight();
    this.hideWarning();
    this.loggingOut = false;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  /** Continue during grace warning — revive shared lastAction via touch. */
  resumeSession(): void {
    if (this.loggingOut || !this.started) {
      return;
    }
    this.lastTouchSentAt = 0;
    // Stop the grace logout timer immediately so a slow touch cannot lose the race.
    this.hideWarning();
    this.ping('touch', true);
  }

  private servicesBaseUrl(): string {
    return this.configService.get.call(this.configService, 'platform').servicesBaseUrl;
  }

  private isTabInteractive(): boolean {
    if (typeof document === 'undefined') {
      return false;
    }
    if (document.hidden) {
      return false;
    }
    if (typeof document.hasFocus === 'function' && !document.hasFocus()) {
      return false;
    }
    return true;
  }

  private bindActivityListeners(): void {
    this.unbindActivityListeners();
    this.activityAbort = new AbortController();
    const { signal } = this.activityAbort;
    for (const eventName of ACTIVITY_EVENTS) {
      document.addEventListener(eventName, this.onUserActivity, {
        signal,
        passive: true,
      });
    }
    document.addEventListener('visibilitychange', this.onVisibilityChange, {
      signal,
    });
  }

  private unbindActivityListeners(): void {
    this.activityAbort?.abort();
    this.activityAbort = null;
  }

  private readonly onUserActivity = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    if (target?.closest?.('.idle-session-warning')) {
      return;
    }
    if (this._warningVisible()) {
      return;
    }
    this.touchFromActivity();
  };

  private readonly onVisibilityChange = (): void => {
    if (!this.started || this.loggingOut) {
      return;
    }
    if (this.isTabInteractive()) {
      this.touchFromActivity();
    } else {
      this.ping('check');
    }
  };

  private touchFromActivity(): void {
    if (!this.started || this.loggingOut || !this.isTabInteractive()) {
      return;
    }
    if (this._warningVisible()) {
      return;
    }
    const now = Date.now();
    if (now - this.lastTouchSentAt < this.touchThrottleMs) {
      return;
    }
    this.lastTouchSentAt = now;
    this.ping('touch');
  }

  private schedulePoll(): void {
    this.clearPoll();
    this.pollTimer = setInterval(() => {
      if (!this.started || this.loggingOut) {
        return;
      }
      this.ping('check');
    }, this.pollIntervalSec * 1000);
  }

  private clearPoll(): void {
    if (this.pollTimer != null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private clearLocalCountdown(): void {
    if (this.localCountdownTimer != null) {
      clearInterval(this.localCountdownTimer);
      this.localCountdownTimer = null;
    }
  }

  private resolvePollInterval(idleAfter: number): number {
    let poll = Math.floor(idleAfter / 2);
    if (idleAfter <= 120) {
      poll = Math.floor(idleAfter / 3);
    }
    if (poll < MIN_POLL_SECONDS) {
      poll = MIN_POLL_SECONDS;
    }
    if (poll > MAX_POLL_SECONDS) {
      poll = MAX_POLL_SECONDS;
    }
    if (poll >= idleAfter) {
      poll = Math.max(5, Math.floor(idleAfter / 2));
    }
    return poll;
  }

  private ping(mode: IdleHeartbeatMode, forceHideWarningOnSuccess = false): void {
    if (!this.started || this.loggingOut || !this.authService.getBearerToken()) {
      return;
    }
    if (mode === 'check' && this.inFlightMode === 'touch') {
      return;
    }
    const generation = ++this.pingGeneration;
    this.inFlightSub?.unsubscribe();
    this.inFlightMode = mode;
    const headers = new HttpHeaders({ 'X-Skip-Spinner': '1' });
    this.inFlightSub = this.http
      .post<IdleSessionHeartbeatResponse>(
        `${this.servicesBaseUrl()}/auth/session/heartbeat?mode=${mode}`,
        {},
        { headers },
      )
      .subscribe({
        next: (res) => {
          if (generation !== this.pingGeneration) {
            return;
          }
          this.clearInFlight();
          const payload = this.normalizeHeartbeatResponse(res);
          if (!payload) {
            return;
          }
          if (payload.status === 0 || payload.data?.valid === false) {
            this.startGraceWarningCountdown();
            return;
          }
          this.applyStatus(payload.data, forceHideWarningOnSuccess || mode === 'touch');
        },
        error: (err: { status?: number; error?: unknown }) => {
          if (generation !== this.pingGeneration) {
            return;
          }
          this.clearInFlight();
          const payload = this.normalizeHeartbeatResponse(err?.error ?? err);
          if (payload) {
            if (payload.status === 0 || payload.data?.valid === false) {
              this.startGraceWarningCountdown();
            } else {
              this.applyStatus(payload.data, forceHideWarningOnSuccess || mode === 'touch');
            }
            return;
          }
          if (err?.status === 401 || err?.status === 403) {
            this.startGraceWarningCountdown();
          }
        },
      });
  }

  private clearInFlight(): void {
    this.inFlightMode = null;
    this.inFlightSub = null;
  }

  private normalizeHeartbeatResponse(res: unknown): IdleSessionHeartbeatResponse | null {
    if (!res || typeof res !== 'object') {
      return null;
    }
    const r = res as Record<string, unknown>;
    if (r['status'] === 0 || r['status'] === 1) {
      return res as IdleSessionHeartbeatResponse;
    }
    if (r['body']) {
      return this.normalizeHeartbeatResponse(r['body']);
    }
    if (r['error']) {
      return this.normalizeHeartbeatResponse(r['error']);
    }
    return null;
  }

  private applyStatus(
    data: IdleSessionStatus | undefined,
    forceHideWarning = false,
  ): void {
    if (!data) {
      return;
    }
    if (data.valid === false || data.remainingSeconds <= 0) {
      this.startGraceWarningCountdown();
      return;
    }

    if (typeof data.idleAfter === 'number' && data.idleAfter > 0) {
      this.idleAfter = data.idleAfter;
      const nextPoll = this.resolvePollInterval(data.idleAfter);
      if (nextPoll !== this.pollIntervalSec) {
        this.pollIntervalSec = nextPoll;
        this.touchThrottleMs = Math.max(10_000, Math.min(30_000, nextPoll * 1000));
        if (this.started) {
          this.schedulePoll();
        }
      }
    }
    if (typeof data.warningLength === 'number' && data.warningLength > 0) {
      this.warningLength = data.warningLength;
    }

    this._remainingSeconds.set(data.remainingSeconds);

    if (this._inGraceWarning() || forceHideWarning) {
      this.hideWarning();
    }

    if (data.remainingSeconds <= this.warningLength) {
      this.showWarning(data.remainingSeconds);
      this.startLocalCountdown(data.remainingSeconds);
    } else if (forceHideWarning) {
      this.hideWarning();
    }
  }

  private showWarning(seconds: number): void {
    this._countdownSeconds.set(Math.max(0, seconds));
    this._warningVisible.set(true);
  }

  private hideWarning(): void {
    this._inGraceWarning.set(false);
    this._warningVisible.set(false);
    this._countdownSeconds.set(0);
    this.clearLocalCountdown();
  }

  private startLocalCountdown(fromSeconds: number): void {
    if (this.localCountdownTimer != null || this._inGraceWarning()) {
      return;
    }
    let left = fromSeconds;
    this.localCountdownTimer = setInterval(() => {
      left -= 1;
      this._remainingSeconds.set(Math.max(0, left));
      if (left <= 0) {
        this.clearLocalCountdown();
        this.startGraceWarningCountdown();
        return;
      }
      if (left <= this.warningLength) {
        this.showWarning(left);
      }
    }, 1000);
  }

  private startGraceWarningCountdown(): void {
    if (this.loggingOut || this._inGraceWarning()) {
      return;
    }
    this._inGraceWarning.set(true);
    this._remainingSeconds.set(0);
    let graceLeft = GRACE_WARNING_SECONDS;
    this.showWarning(graceLeft);
    this.clearLocalCountdown();
    this.localCountdownTimer = setInterval(() => {
      graceLeft -= 1;
      if (graceLeft <= 0) {
        this.clearLocalCountdown();
        this.triggerLogout();
        return;
      }
      this.showWarning(graceLeft);
    }, 1000);
  }

  private triggerLogout(): void {
    if (this.loggingOut) {
      return;
    }
    this.loggingOut = true;
    this.stop();
    this.authService.sessionExpiry();
  }
}
